export const FORM_TYPES = {
  SURVEY: 'survey',
  POINTS: 'points'
};

export const FORM_TYPE_LABELS = {
  [FORM_TYPES.SURVEY]: 'Survey',
  [FORM_TYPES.POINTS]: 'Points (scored)'
};

export const ANSWER_MODES = {
  FREE: 'free',
  CHOICES: 'choices',
  TEXT: 'text',
  NUMBER_EXACT: 'number_exact',
  NUMBER_RANGE: 'number_range',
  DATE_EXACT: 'date_exact',
  DATE_RANGE: 'date_range',
  MANUAL: 'manual'
};

export const ANSWER_MODE_LABELS = {
  [ANSWER_MODES.FREE]: 'Free — any answer',
  [ANSWER_MODES.CHOICES]: 'Correct option(s)',
  [ANSWER_MODES.TEXT]: 'Text match',
  [ANSWER_MODES.NUMBER_EXACT]: 'Is exactly',
  [ANSWER_MODES.NUMBER_RANGE]: 'Between (range)',
  [ANSWER_MODES.DATE_EXACT]: 'Is exactly',
  [ANSWER_MODES.DATE_RANGE]: 'Date range',
  [ANSWER_MODES.MANUAL]: 'Manual — grader awards points'
};

export const TEXT_MATCH_TYPES = {
  INCLUDES: 'includes',
  EQUALS: 'equals'
};

export const TEXT_MATCH_LABELS = {
  [TEXT_MATCH_TYPES.INCLUDES]: 'Includes text',
  [TEXT_MATCH_TYPES.EQUALS]: 'Equals exactly'
};

export const CHOICE_QUESTION_TYPES = new Set(['select', 'single-choice', 'multiple-choice']);
export const TEXT_QUESTION_TYPES = new Set(['varchar', 'textarea', 'text']);
export const NUMBER_QUESTION_TYPES = new Set(['number']);
export const DATE_QUESTION_TYPES = new Set(['date']);
export const UPLOAD_QUESTION_TYPES = new Set(['file-upload']);

export function defaultNumberRule() {
  return { exact: '', min: '', max: '' };
}

export function defaultDateRule() {
  return { exact: '', min: '', max: '' };
}

export function getAnswerModesForQuestionType(type) {
  if (UPLOAD_QUESTION_TYPES.has(type)) {
    return [ANSWER_MODES.FREE, ANSWER_MODES.MANUAL];
  }
  const appendManual = (modes) =>
    modes.includes(ANSWER_MODES.MANUAL) ? modes : [...modes, ANSWER_MODES.MANUAL];
  if (CHOICE_QUESTION_TYPES.has(type)) {
    return appendManual([ANSWER_MODES.FREE, ANSWER_MODES.CHOICES]);
  }
  if (TEXT_QUESTION_TYPES.has(type)) {
    return appendManual([ANSWER_MODES.FREE, ANSWER_MODES.TEXT]);
  }
  if (NUMBER_QUESTION_TYPES.has(type)) {
    return appendManual([ANSWER_MODES.FREE, ANSWER_MODES.NUMBER_EXACT, ANSWER_MODES.NUMBER_RANGE]);
  }
  if (DATE_QUESTION_TYPES.has(type)) {
    return appendManual([ANSWER_MODES.FREE, ANSWER_MODES.DATE_EXACT, ANSWER_MODES.DATE_RANGE]);
  }
  return appendManual([ANSWER_MODES.FREE]);
}

export function clampAnswerModeForQuestionType(type, answerMode) {
  const allowed = getAnswerModesForQuestionType(type);
  if (allowed.includes(answerMode)) return answerMode;
  return defaultScoringForQuestion({ type }).answerMode;
}

export function isPointsForm(formType) {
  return String(formType || '').toLowerCase() === FORM_TYPES.POINTS;
}

export function defaultScoringForQuestion(question = {}) {
  const base = {
    correctOptions: [],
    textRule: { match: TEXT_MATCH_TYPES.INCLUDES, value: '', caseSensitive: false },
    numberRule: defaultNumberRule(),
    dateRule: defaultDateRule()
  };
  if (CHOICE_QUESTION_TYPES.has(question.type)) {
    return { ...base, answerMode: ANSWER_MODES.CHOICES };
  }
  if (TEXT_QUESTION_TYPES.has(question.type)) {
    return { ...base, answerMode: ANSWER_MODES.TEXT };
  }
  if (NUMBER_QUESTION_TYPES.has(question.type)) {
    return { ...base, answerMode: ANSWER_MODES.NUMBER_EXACT };
  }
  if (DATE_QUESTION_TYPES.has(question.type)) {
    return { ...base, answerMode: ANSWER_MODES.DATE_EXACT };
  }
  if (UPLOAD_QUESTION_TYPES.has(question.type)) {
    return { ...base, answerMode: ANSWER_MODES.FREE };
  }
  return { ...base, answerMode: ANSWER_MODES.FREE };
}

export function normalizeLegacyFormType(type) {
  const t = String(type || '').toLowerCase();
  if (t === FORM_TYPES.POINTS) return FORM_TYPES.POINTS;
  return FORM_TYPES.SURVEY;
}

function normalizeScoringFields(scoring, questionType) {
  const base = defaultScoringForQuestion({ type: questionType });
  const stored = scoring && typeof scoring === 'object' ? scoring : {};
  const answerMode = clampAnswerModeForQuestionType(questionType, stored.answerMode || base.answerMode);
  const storedNumber = stored.numberRule && typeof stored.numberRule === 'object' ? stored.numberRule : {};
  const storedDate = stored.dateRule && typeof stored.dateRule === 'object' ? stored.dateRule : {};
  return {
    answerMode,
    correctOptions: Array.isArray(stored.correctOptions)
      ? stored.correctOptions.map(String).filter(Boolean)
      : [],
    textRule: {
      match: stored.textRule?.match === TEXT_MATCH_TYPES.EQUALS ? TEXT_MATCH_TYPES.EQUALS : TEXT_MATCH_TYPES.INCLUDES,
      value: String(stored.textRule?.value ?? ''),
      caseSensitive: stored.textRule?.caseSensitive === true
    },
    numberRule: {
      exact: storedNumber.exact ?? '',
      min: storedNumber.min ?? '',
      max: storedNumber.max ?? ''
    },
    dateRule: {
      exact: storedDate.exact ?? '',
      min: storedDate.min ?? '',
      max: storedDate.max ?? ''
    }
  };
}

/** Persist only scoring fields relevant to the active answer mode. */
export function compactScoringForStorage(scoring, questionType) {
  if (!scoring || typeof scoring !== 'object') return null;
  const normalized = normalizeScoringFields(scoring, questionType);
  const { answerMode } = normalized;
  const compact = { answerMode };

  switch (answerMode) {
    case ANSWER_MODES.CHOICES:
      compact.correctOptions = normalized.correctOptions;
      break;
    case ANSWER_MODES.TEXT:
      compact.textRule = normalized.textRule;
      break;
    case ANSWER_MODES.NUMBER_EXACT:
      compact.numberRule = { exact: normalized.numberRule.exact };
      break;
    case ANSWER_MODES.NUMBER_RANGE:
      compact.numberRule = {
        min: normalized.numberRule.min,
        max: normalized.numberRule.max
      };
      break;
    case ANSWER_MODES.DATE_EXACT:
      compact.dateRule = { exact: normalized.dateRule.exact };
      break;
    case ANSWER_MODES.DATE_RANGE:
      compact.dateRule = {
        min: normalized.dateRule.min,
        max: normalized.dateRule.max
      };
      break;
    case ANSWER_MODES.FREE:
    case ANSWER_MODES.MANUAL:
    default:
      break;
  }

  return compact;
}

/** Strip scoring noise and survey-only fields before persisting a question. */
export function compactQuestionForStorage(question, formType) {
  if (!question || typeof question !== 'object') return question;
  const q = { ...question };

  if (!isPointsForm(formType)) {
    delete q.scoring;
    delete q.points;
    delete q.awardAttemptPoints;
    delete q.attemptPoints;
    return q;
  }

  if (q.scoring) {
    q.scoring = compactScoringForStorage(q.scoring, q.type);
  }

  return q;
}
